import React, { useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { closeOutline, cloudUploadOutline, colorPaletteOutline, downloadOutline } from 'ionicons/icons';
import TutorialMascot from '../Tutorial/game/TutorialMascot';
import { TUTORIAL_DESIGNS, TUTORIAL_TOTAL_MINUTES } from '../../tutorials';
import { isNativeiOS } from '../../lib/utils/platform';
import './EmbroideryWelcomeModal.css';

const WHAT_IT_DOES = [
  {
    icon: cloudUploadOutline,
    title: 'Start from a picture',
    desc: 'Upload artwork or describe it and let the AI draw it.',
  },
  {
    icon: colorPaletteOutline,
    title: 'Shape the stitches',
    desc: 'Every colour becomes a layer you can fill, satin or outline.',
  },
  {
    icon: downloadOutline,
    title: 'Send it to your machine',
    desc: 'Export DST, PES, JEF and the rest — sized to your hoop.',
  },
];

/**
 * First-run greeting for EmbroideryTrace.
 *
 * The app opens on an empty file list, which says nothing about what the
 * software is for or where to learn it, so this covers both: three lines on the
 * workflow, then a route into the Academy while the user is still deciding what
 * to do first. Dismissible — someone who arrived with a file in hand shouldn't
 * have to read anything.
 *
 * The Academy can't run on phones or the iOS app (`startTutorial` refuses
 * below 768px), so there the offer is left out rather than dangled.
 */
const EmbroideryWelcomeModal = ({ isVisible, onClose, onStartAcademy, userName }) => {
  const academyAvailable = !isNativeiOS() && window.innerWidth > 768;
  const firstLesson = TUTORIAL_DESIGNS[0];

  useEffect(() => {
    if (!isVisible) return;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div className="emb-welcome-backdrop" onClick={onClose}>
      <div
        className="emb-welcome-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="emb-welcome-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="emb-welcome-close" onClick={onClose} aria-label="Close">
          <IonIcon icon={closeOutline} />
        </button>

        <div className="emb-welcome-mascot">
          <TutorialMascot talking={false} cheering={false} size={86} />
        </div>

        <h1 className="emb-welcome-title" id="emb-welcome-title">
          Welcome to EmbroideryTrace{userName ? `, ${userName}` : ''}
        </h1>
        <p className="emb-welcome-lede">
          It turns any image into a stitch file your embroidery machine can sew.
        </p>

        <ul className="emb-welcome-list">
          {WHAT_IT_DOES.map((item) => (
            <li className="emb-welcome-item" key={item.title}>
              <span className="emb-welcome-item-icon">
                <IonIcon icon={item.icon} />
              </span>
              <span className="emb-welcome-item-text">
                <span className="emb-welcome-item-title">{item.title}</span>
                <span className="emb-welcome-item-desc">{item.desc}</span>
              </span>
            </li>
          ))}
        </ul>

        {academyAvailable ? (
          <>
            <div className="emb-welcome-academy">
              <div className="emb-welcome-academy-text">
                <span className="emb-welcome-academy-title">New to digitizing? Start in the Academy</span>
                <span className="emb-welcome-academy-desc">
                  {TUTORIAL_DESIGNS.length} guided lessons, about {TUTORIAL_TOTAL_MINUTES} minutes for
                  the whole set. Each one ends with a file you could stitch.
                </span>
              </div>
            </div>

            <button type="button" className="emb-welcome-primary" onClick={onStartAcademy}>
              Start lesson 1 · {firstLesson.minutes} min
            </button>
            <button type="button" className="emb-welcome-secondary" onClick={onClose}>
              I'll look around first
            </button>
          </>
        ) : (
          <button type="button" className="emb-welcome-primary" onClick={onClose}>
            Get started
          </button>
        )}
      </div>
    </div>
  );
};

export default EmbroideryWelcomeModal;
